import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { waLink, PRESET } from "@/lib/whatsapp";

export const Header = ({ t, lang }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const switchLang = (next) => {
    const currentPath = location.pathname;
    // Preserve blog routes when switching language
    const blogMatch = currentPath.match(/^\/(en|es)\/blog\/(.+)$/);
    if (blogMatch) {
      navigate(`/${next}/blog/${blogMatch[2]}`);
    } else {
      navigate(`/${next}${location.hash || ""}`);
    }
  };

  // If on a subpage (blog, etc.), links go to landing page with hash
  const isLanding = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;
  const base = isLanding ? "" : `/${lang}`;
  const links = [
    { href: `${base}#work`, label: t.nav.work, id: "nav-work" },
    { href: `${base}#artists`, label: t.nav.artists, id: "nav-artists" },
    { href: `${base}#quote`, label: t.nav.quote, id: "nav-quote" },
    { href: `${base}#blog`, label: t.nav.blog || "Blog", id: "nav-blog" },
    { href: `${base}#faq`, label: t.nav.faq, id: "nav-faq" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-bendito-bg/85 backdrop-blur-md border-b border-bendito-border"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-4 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-bendito-muted hover:text-bendito-ink transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center font-mono text-[11px] tracking-[0.25em] border border-bendito-border">
            <button
              onClick={() => switchLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en" ? "bg-bendito-orange text-black" : "text-bendito-muted hover:text-bendito-ink"
              }`}
              data-testid="lang-en"
            >
              EN
            </button>
            <button
              onClick={() => switchLang("es")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "es" ? "bg-bendito-orange text-black" : "text-bendito-muted hover:text-bendito-ink"
              }`}
              data-testid="lang-es"
            >
              ES
            </button>
          </div>

          <a
            href={waLink(lang === "es" ? PRESET.generalEs : PRESET.generalEn)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex bg-bendito-orange hover:bg-bendito-orangeDark text-black font-heading tracking-[0.2em] text-sm px-5 py-2.5 transition-colors"
            data-testid="header-book-btn"
          >
            {t.nav.book}
          </a>

          <button
            className="lg:hidden text-bendito-ink p-[11px]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-bendito-bg border-t border-bendito-border"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-5 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-heading text-2xl text-bendito-ink tracking-wider"
                data-testid={`mobile-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => switchLang("en")}
                className={`px-3 py-1.5 font-mono text-xs border ${
                  lang === "en" ? "bg-bendito-orange text-black border-bendito-orange" : "border-bendito-border text-bendito-muted"
                }`}
                data-testid="mobile-lang-en"
              >
                EN
              </button>
              <button
                onClick={() => switchLang("es")}
                className={`px-3 py-1.5 font-mono text-xs border ${
                  lang === "es" ? "bg-bendito-orange text-black border-bendito-orange" : "border-bendito-border text-bendito-muted"
                }`}
                data-testid="mobile-lang-es"
              >
                ES
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
